/**
 * Filial-Einrichtungs-Assistent (Jun 2026) — der Hauptsitz pflegt ALLE
 * Stammdaten einer Filiale Schritt für Schritt (wie die Registrierung).
 * Vom Hauptsitz zentral gesteuerte Felder (aktive Master-Switches) sind
 * gesperrt und als „vom Hauptsitz übernommen" markiert.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Lock, ChevronLeft, ChevronRight, LogIn, Mail, CheckCircle2, Clock, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { useEmployees, EmployeeSelect } from "./_shared";

const WEEKDAYS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];
const defaultHours = () => WEEKDAYS.map((_, i) => ({ day: i, closed: i >= 5, open: "09:00", close: "18:00" }));
const hoursToText = (rows) => (rows || []).map((r, i) => `${WEEKDAYS[r.day ?? i]} ${r.closed ? "geschlossen" : `${r.open}–${r.close} Uhr`}`).join("\n");

const STEPS = [
    { key: "stammdaten", label: "Stammdaten" },
    { key: "kontakt", label: "Kontakt" },
    { key: "oeffnungszeiten", label: "Öffnungszeiten" },
    { key: "steuer", label: "Steuer" },
    { key: "bank", label: "Bank" },
    { key: "rechtliches", label: "Rechtliches" },
];

// Auf Modul-Ebene definiert (NICHT im Render!) — sonst remountet React das Input
// bei jedem Tastendruck und der Cursor springt raus.
const fmtDateDe = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch { return iso; }
};

const InviteStatusBanner = ({ status }) => {
    if (!status || status.state === "none") return null;
    const cfg = {
        accepted: { icon: CheckCircle2, cls: "text-emerald-700 bg-emerald-50 border-emerald-200",
            text: `Passwort gesetzt am ${fmtDateDe(status.password_set_at)} — Login aktiv.` },
        invited: { icon: Clock, cls: "text-amber-700 bg-amber-50 border-amber-200",
            text: `Eingeladen am ${fmtDateDe(status.invited_at)} · noch nicht angenommen.` },
        expired: { icon: AlertTriangle, cls: "text-red-700 bg-red-50 border-red-200",
            text: `Einladung vom ${fmtDateDe(status.invited_at)} ist abgelaufen — bitte erneut senden.` },
        active: { icon: CheckCircle2, cls: "text-emerald-700 bg-emerald-50 border-emerald-200",
            text: "Login bereits eingerichtet." },
    }[status.state];
    if (!cfg) return null;
    const Icon = cfg.icon;
    return (
        <div className={`mt-3 flex items-start gap-2 text-[12px] font-bold border rounded-sm px-2.5 py-1.5 ${cfg.cls}`} data-testid="wizard-invite-status">
            <Icon className="w-4 h-4 mt-0.5 shrink-0" /><span>{cfg.text}</span>
        </div>
    );
};

const WizardLockedNote = ({ field, locked }) => (locked || []).includes(field) ? (
    <span className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-sm ml-2" data-testid={`wizard-locked-${field}`}>
        <Lock className="w-2.5 h-2.5" /> vom Hauptsitz übernommen
    </span>
) : null;

const WizardField = ({ field, label, type = "text", className = "", maxLength, form, set, locked }) => {
    const disabled = (locked || []).includes(field);
    return (
        <div className={className}>
            <Label className="text-xs font-bold uppercase tracking-wider text-gray-500 flex items-center">{label}<WizardLockedNote field={field} locked={locked} /></Label>
            <Input type={type} value={form[field] || ""} onChange={set(field)} disabled={disabled} maxLength={maxLength}
                className="rounded-sm mt-1 disabled:bg-gray-100 disabled:text-gray-400" data-testid={`wizard-${field}`} />
        </div>
    );
};

export default function BranchSetupWizard({ branchId, open, onClose, onSaved }) {
    const navigate = useNavigate();
    const { logout } = useAuth();
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [step, setStep] = useState(0);
    const [form, setForm] = useState({});
    const [locked, setLocked] = useState([]);
    const allEmployees = useEmployees();
    const [inviting, setInviting] = useState(false);
    const [inviteSent, setInviteSent] = useState(false);
    const [inviteStatus, setInviteStatus] = useState(null);
    // Nur Mitarbeiter DIESER Filiale (org-weit ausschließen — Jun-2026, User-Wunsch).
    const branchEmployees = (allEmployees || []).filter((e) => e.employer_dealer_id === branchId);

    const loadInviteStatus = async () => {
        if (!branchId) return;
        try {
            const r = await api.get(`/org/branches/${branchId}/invite-status`);
            setInviteStatus(r.data || null);
        } catch { /* still optional */ }
    };

    const sendInvite = async () => {
        const email = (form.login_email || "").trim();
        if (!email || !email.includes("@")) {
            toast.error("Bitte zuerst eine gültige Login-E-Mail eingeben."); return;
        }
        setInviting(true);
        try {
            const r = await api.post(`/org/branches/${branchId}/send-invite`, { login_email: email });
            setInviteSent(true);
            setForm((p) => ({ ...p, has_login: true }));
            if (r.data?.invite_status) setInviteStatus(r.data.invite_status);
            else loadInviteStatus();
            toast.success("Einladungs-Mail gesendet — die Filiale setzt ihr Passwort selbst.");
            if (r.data?.invite_link) console.log("DEV Einladungs-Link:", r.data.invite_link);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Einladung konnte nicht gesendet werden.");
        }
        setInviting(false);
    };

    useEffect(() => {
        if (!open || !branchId) return;
        setStep(0);
        setLoading(true);
        api.get(`/org/branches/${branchId}/full`).then((r) => {
            const d = r.data || {};
            setLocked(d.locked_fields || []);
            setForm({
                ...d,
                country: d.country || "Deutschland",
                // Straße & Hausnr. werden — wie in den Stammdaten — als EIN Feld
                // geführt (vermeidet Übernahme-Probleme zur Registrierung). Eine
                // evtl. separat gespeicherte Hausnummer wird hier zusammengeführt.
                street: [d.street, d.house_number].filter(Boolean).join(" ").trim(),
                house_number: "",
                login_email: d.login_email || d.contact_email || "",
                _initial_login_email: d.login_email || "",
                login_password: "",
                sales_hours_enabled: !!d.sales_hours_enabled,
                sales_hours_structured: (Array.isArray(d.sales_hours_structured) && d.sales_hours_structured.length === 7)
                    ? d.sales_hours_structured.map((x, i) => ({ day: i, closed: !!x.closed, open: x.open || "09:00", close: x.close || "18:00" }))
                    : defaultHours(),
            });
        }).catch(() => toast.error("Filialdaten konnten nicht geladen werden"))
            .finally(() => setLoading(false));
        setInviteSent(false);
        setInviteStatus(null);
        loadInviteStatus();
    }, [open, branchId]);

    const isLocked = (f) => locked.includes(f);
    const set = (k) => (e) => {
        const v = e?.target?.type === "checkbox" ? e.target.checked : e.target.value;
        setForm((p) => ({ ...p, [k]: k === "site_code" ? String(v).toUpperCase().replace(/[^A-Z]/g, "").slice(0, 4) : v }));
    };
    const setHourRow = (i, key, val) => setForm((p) => ({
        ...p, sales_hours_structured: (p.sales_hours_structured || defaultHours()).map((r, idx) => idx === i ? { ...r, [key]: val } : r),
    }));

    const save = async () => {
        const sc = form.site_code || "";
        if (!sc || sc.length !== 4) {
            toast.error("Standort-Code muss genau 4 Großbuchstaben haben."); setStep(0); return;
        }
        if (form.login_password && form.login_password.length < 8) {
            toast.error("Das Login-Passwort muss mindestens 8 Zeichen haben."); setStep(1); return;
        }
        setSaving(true);
        try {
            const payload = {};
            const editable = [
                "company_name", "street", "house_number", "postal_code", "city", "country", "site_code",
                "contact_first_name", "contact_last_name", "contact_email", "phone_office", "phone_mobile",
                "collective_phone", "collective_email",
                "ansprechpartner_employee_id", "ansprechpartner_same_as_gf",
                "sales_hours_enabled", "sales_hours", "tax_id", "vat_id", "tax_number", "handelsregister",
                "register_court", "register_number", "company_seat", "managing_director",
                "bank_iban", "bank_bic", "bank_account_holder", "bank_name", "imprint_text",
            ];
            editable.forEach((f) => { if (!isLocked(f) && form[f] !== undefined) payload[f] = form[f]; });
            if (form.sales_hours_enabled && Array.isArray(form.sales_hours_structured)) {
                payload.sales_hours = hoursToText(form.sales_hours_structured);
            }
            // Login-Zugang der Filiale (Hauptsitz vergibt/ändert) — Jun-2026.
            if (form.login_email !== undefined) payload.login_email = form.login_email;
            if (form.login_password) payload.login_password = form.login_password;
            const res = await api.patch(`/org/branches/${branchId}/full`, payload);
            const filialeEmail = res.data?.login_email || payload.login_email || form.login_email || form.contact_email || "";
            // 2c (User-Wunsch): Nach dem Speichern wird der Hauptsitz ausgeloggt und
            // direkt zur Anmeldeseite der Filiale weitergeleitet, um die restlichen
            // (nicht org-weit erzwungenen) Daten als Filiale zu ergänzen.
            toast.success("Filiale gespeichert — Sie werden zur Anmeldung der Filiale weitergeleitet.");
            onSaved?.();
            onClose?.();
            await logout();
            navigate(filialeEmail ? `/login?email=${encodeURIComponent(filialeEmail)}` : "/login");
            return;
        } catch (e) {
            toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
        } finally { setSaving(false); }
    };

    const current = STEPS[step];

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
            <DialogContent className="sm:max-w-[640px] max-h-[92vh] overflow-y-auto" data-testid="branch-wizard-dialog">
                <DialogHeader>
                    <DialogTitle>Filiale einrichten — {form.company_name || "Filiale"}{form.site_code ? ` · ${form.site_code}` : ""}</DialogTitle>
                </DialogHeader>

                {/* Schritt-Indikator */}
                <div className="flex items-center gap-1 flex-wrap" data-testid="wizard-steps">
                    {STEPS.map((s, i) => (
                        <button key={s.key} type="button" onClick={() => setStep(i)}
                            className={`text-[11px] font-bold px-2 py-1 rounded-sm transition-colors ${i === step ? "bg-[#2563EB] text-white" : "bg-gray-100 text-gray-500 hover:bg-gray-200"}`}
                            data-testid={`wizard-step-${s.key}`}>
                            {i + 1}. {s.label}
                        </button>
                    ))}
                </div>

                {loading ? (
                    <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#2563EB]" /></div>
                ) : (
                    <div className="space-y-3 mt-2" data-testid={`wizard-panel-${current.key}`}>
                        {current.key === "stammdaten" && (<>
                            <WizardField field="company_name" label="Firmierung" form={form} set={set} locked={locked} />
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                <WizardField field="street" label="Straße & Hausnr." className="sm:col-span-3" form={form} set={set} locked={locked} />
                                <WizardField field="postal_code" label="PLZ" form={form} set={set} locked={locked} />
                                <WizardField field="city" label="Ort" className="sm:col-span-2" form={form} set={set} locked={locked} />
                                <WizardField field="country" label="Land" form={form} set={set} locked={locked} />
                                <WizardField field="site_code" label="Standort-Code (genau 4 Großb.)" maxLength={4} form={form} set={set} locked={locked} />
                            </div>
                        </>)}
                        {current.key === "kontakt" && (<>
                            {/* Ansprechpartner aus Mitarbeitern wählen (wie in den Einstellungen) — Jun-2026. */}
                            <div className="mb-1" data-testid="wizard-ansprechpartner-block">
                                <Label className="text-xs font-bold uppercase tracking-wider text-gray-500 flex items-center">
                                    Ansprechpartner aus Mitarbeitern wählen
                                </Label>
                                <div className="mt-1">
                                    <EmployeeSelect
                                        employees={branchEmployees}
                                        value={form.ansprechpartner_employee_id}
                                        onSelect={(id) => {
                                            const emp = branchEmployees.find((e) => e.id === id);
                                            setForm((p) => {
                                                const next = { ...p, ansprechpartner_employee_id: id || "", ansprechpartner_same_as_gf: false };
                                                if (emp) {
                                                    const parts = (emp.name || "").trim().split(/\s+/);
                                                    const fn = emp.first_name || (parts.length > 1 ? parts.slice(0, -1).join(" ") : parts[0]) || "";
                                                    const ln = emp.last_name || (parts.length > 1 ? parts[parts.length - 1] : "");
                                                    if (fn) next.contact_first_name = fn;
                                                    if (ln) next.contact_last_name = ln;
                                                    if (emp.email && !emp.email.endsWith("@local.twowheels.info")) next.contact_email = emp.email;
                                                }
                                                return next;
                                            });
                                        }}
                                        placeholder="— Mitarbeiter wählen (optional) —"
                                        testId="wizard-ansprechpartner-select"
                                    />
                                </div>
                                <p className="text-[11px] text-gray-500 mt-1">Übernimmt Vor-/Nachname und E-Mail in die Kontaktfelder. Manuelle Eingabe bleibt möglich.</p>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <WizardField field="contact_first_name" label="Vorname (Ansprechpartner)" form={form} set={set} locked={locked} />
                                <WizardField field="contact_last_name" label="Nachname" form={form} set={set} locked={locked} />
                                <WizardField field="contact_email" label="Kontakt-E-Mail" type="email" form={form} set={set} locked={locked} />
                                <WizardField field="phone_office" label="Telefon (Geschäft)" form={form} set={set} locked={locked} />
                                <WizardField field="phone_mobile" label="Handynummer" form={form} set={set} locked={locked} />
                                <WizardField field="collective_phone" label="Sammelrufnummer (Standort)" form={form} set={set} locked={locked} />
                                <WizardField field="collective_email" label="Sammel-E-Mail (Standort)" type="email" form={form} set={set} locked={locked} />
                            </div>
                            {/* Login-Zugang der Filiale — Einladung mit Selbst-Setzen-Link (Jun-2026, User-Wunsch). */}
                            <div className="mt-4 pt-4 border-t border-gray-200" data-testid="wizard-login-block">
                                <div className="text-[11px] font-black uppercase tracking-wider text-[#2563EB] mb-1">Login-Zugang der Filiale</div>
                                <p className="text-[11px] text-gray-500 mb-3">
                                    Die Filiale meldet sich mit dieser E-Mail-Adresse an. {form.has_login ? "Login bereits eingerichtet." : "Noch kein Login gesetzt."} Statt ein Passwort zu vergeben, senden Sie eine <strong>Einladungs-Mail</strong> — die Filiale setzt ihr Passwort selbst über einen sicheren Link.
                                </p>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-end">
                                    <div>
                                        <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Login-E-Mail</Label>
                                        <Input type="email" value={form.login_email || ""} onChange={set("login_email")}
                                            className="rounded-sm mt-1" data-testid="wizard-login_email" placeholder="filiale@firma.de" />
                                    </div>
                                    <div>
                                        <Button type="button" onClick={sendInvite} disabled={inviting}
                                            className="w-full bg-[#2563EB] hover:bg-[#1d4ed8] text-white rounded-sm font-bold"
                                            data-testid="wizard-send-invite">
                                            {inviting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Mail className="w-4 h-4 mr-1" />}
                                            {(inviteSent || inviteStatus?.state === "invited" || inviteStatus?.state === "expired") ? "Erinnerung senden" : "Einladungs-Mail senden"}
                                        </Button>
                                        {inviteSent && <p className="text-[11px] text-emerald-600 font-bold mt-1" data-testid="wizard-invite-sent">Einladung gesendet ✓</p>}
                                    </div>
                                </div>
                                {/* Einladungs-Status je Filiale (P0, Jun-2026) */}
                                <InviteStatusBanner status={inviteStatus} />
                            </div>
                        </>)}
                        {current.key === "oeffnungszeiten" && (
                            <div>
                                <label className="flex items-center gap-2 text-sm font-bold cursor-pointer">
                                    <input type="checkbox" checked={!!form.sales_hours_enabled} onChange={set("sales_hours_enabled")} data-testid="wizard-sales_hours_enabled" />
                                    Öffnungszeiten anzeigen
                                </label>
                                {form.sales_hours_enabled && (
                                    <div className="space-y-1.5 mt-2" data-testid="wizard-hours-grid">
                                        {(form.sales_hours_structured || []).map((r, i) => (
                                            <div key={i} className="flex items-center gap-2 text-sm">
                                                <span className="w-7 font-bold text-gray-700">{WEEKDAYS[i]}</span>
                                                <label className="flex items-center gap-1 text-xs text-gray-600 w-24 shrink-0">
                                                    <input type="checkbox" checked={!!r.closed} onChange={(e) => setHourRow(i, "closed", e.target.checked)} data-testid={`wizard-hours-closed-${i}`} />
                                                    geschlossen
                                                </label>
                                                {!r.closed ? (
                                                    <div className="flex items-center gap-1.5">
                                                        <Input type="time" value={r.open} onChange={(e) => setHourRow(i, "open", e.target.value)} className="rounded-sm h-8 w-28 font-mono-num" />
                                                        <span className="text-gray-400">–</span>
                                                        <Input type="time" value={r.close} onChange={(e) => setHourRow(i, "close", e.target.value)} className="rounded-sm h-8 w-28 font-mono-num" />
                                                    </div>
                                                ) : <span className="text-xs text-gray-400 italic">geschlossen</span>}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                        {current.key === "steuer" && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <WizardField field="tax_id" label="Steuernummer (intern)" form={form} set={set} locked={locked} />
                                <WizardField field="vat_id" label="USt-IdNr." form={form} set={set} locked={locked} />
                                <WizardField field="tax_number" label="Steuernummer" form={form} set={set} locked={locked} />
                                <WizardField field="handelsregister" label="Handelsregister" form={form} set={set} locked={locked} />
                                <WizardField field="register_court" label="Registergericht" form={form} set={set} locked={locked} />
                                <WizardField field="register_number" label="Registernummer" form={form} set={set} locked={locked} />
                                <WizardField field="company_seat" label="Firmensitz" form={form} set={set} locked={locked} />
                                <WizardField field="managing_director" label="Geschäftsführer" form={form} set={set} locked={locked} />
                            </div>
                        )}
                        {current.key === "bank" && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <WizardField field="bank_account_holder" label="Kontoinhaber" className="sm:col-span-2" form={form} set={set} locked={locked} />
                                <WizardField field="bank_iban" label="IBAN" form={form} set={set} locked={locked} />
                                <WizardField field="bank_bic" label="BIC" form={form} set={set} locked={locked} />
                                <WizardField field="bank_name" label="Bank" className="sm:col-span-2" form={form} set={set} locked={locked} />
                            </div>
                        )}
                        {current.key === "rechtliches" && (
                            <div>
                                <Label className="text-xs font-bold uppercase tracking-wider text-gray-500 flex items-center">Impressum<WizardLockedNote field="imprint_text" locked={locked} /></Label>
                                <Textarea value={form.imprint_text || ""} onChange={set("imprint_text")} disabled={isLocked("imprint_text")} rows={8}
                                    className="rounded-sm mt-1 font-mono-num text-xs disabled:bg-gray-100 disabled:text-gray-400" data-testid="wizard-imprint_text" />
                                <p className="text-[11px] text-gray-500 mt-1">AGB, Widerruf & weitere Rechtsdokumente werden weiterhin zentral verwaltet.</p>
                            </div>
                        )}
                    </div>
                )}

                <DialogFooter className="flex sm:justify-between gap-2">
                    <Button variant="outline" onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0 || saving} className="rounded-sm" data-testid="wizard-prev">
                        <ChevronLeft className="w-4 h-4 mr-1" /> Zurück
                    </Button>
                    <div className="flex gap-2">
                        <Button variant="outline" onClick={() => onClose?.()} disabled={saving} className="rounded-sm" data-testid="wizard-cancel">Abbrechen</Button>
                        {step < STEPS.length - 1 ? (
                            <Button onClick={() => setStep((s) => Math.min(STEPS.length - 1, s + 1))} disabled={saving} className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-bold rounded-sm" data-testid="wizard-next">
                                Weiter <ChevronRight className="w-4 h-4 ml-1" />
                            </Button>
                        ) : (
                            <Button onClick={save} disabled={saving} className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-bold rounded-sm" data-testid="wizard-save">
                                {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <LogIn className="w-4 h-4 mr-1" />} Speichern & zur Filiale-Anmeldung
                            </Button>
                        )}
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
